import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../singleton/axios';

// Types for the response
export interface SharesBasis {
  source: 'sec' | 'fmp-shares-float' | 'derived';
  asOf: string | null;
  filedAt?: string;
  form?: string;
  accession?: string;
  rejected?: string;
}

interface ProfileMetrics {
  marketCap: number;
  enterpriseValue: number;
  sharesOutstanding: number;
  // Absent on responses cached before the resolver existed.
  sharesBasis?: SharesBasis | null;
  employees: number;
  sector: string;
  industry: string;
}

interface ValuationMetrics {
  peRatio: number;
  forwardPE: number | null;
  forwardPE2Y: number | null;
  // Null when the provider's PEG fails validation; absent on responses
  // cached before it existed.
  pegRatio?: number | null;
  priceToBook: number;
  priceToSales: number;
  evToSales: number;
  evToEBITDA: number;
  priceToFCF: number;
}

interface ProfitabilityMetrics {
  grossMargin: number;
  operatingMargin: number;
  netMargin: number;
  fcfMargin: number;
}


interface FinancialHealthMetrics {
  cash: number;
  totalDebt: number;
  netDebt: number;
  debtToEquity: number;
  interestCoverage: number;
}

interface DividendMetrics {
  dividendYield: number;
  payoutRatio: number;
  dividendPerShare: number;
  dividendGrowth3Y: number;
  dividendGrowth5Y: number;
}

interface GrowthMetrics {
  revenueCAGR3Y: number;
  revenueCAGR5Y: number;
  epsCAGR3Y: number;
  epsCAGR5Y: number;
}

interface DataAvailability {
  profile: boolean;
  ratiosTTM: boolean;
  keyMetricsTTM: boolean;
  financialGrowth: boolean;
  balanceSheet: boolean;
  cashFlow: boolean;
}

interface CompanyKeyStatisticsMetadata {
  fetchedAt: string;
  dataAvailability: DataAvailability;
  errors?: string[];
}

export interface CompanyKeyStatistics {
  ticker: string;
  profile: ProfileMetrics | null;
  valuation: ValuationMetrics | null;
  profitability: ProfitabilityMetrics | null;
  financialHealth: FinancialHealthMetrics | null;
  dividends: DividendMetrics | null;
  growth: GrowthMetrics | null;
  metadata: CompanyKeyStatisticsMetadata;
}

export const useCompanyKeyStatistics = (ticker: string) => {
  return useQuery<CompanyKeyStatistics, Error>({
    queryKey: ['companyKeyStatistics', ticker],
    queryFn: async () => {
      const { data } = await api.get<CompanyKeyStatistics>('/company-key-statistics', {
        params: { ticker },
      });
      return data;
    },
    enabled: !!ticker,
    refetchOnWindowFocus: false,
    staleTime: 2.5 * 60 * 1000, // 2.5 minutes for fresh key statistics
    gcTime: 60 * 60 * 1000, // 1 hour (renamed from cacheTime in v5)
    retry: 2,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};